import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import { faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoadingOverlay from './LoadingOverlay';
import TextModal from './TextModal';
import { useToast } from './ToastContext';
import './../Styles/Employee.css';

const apiUrl = process.env.REACT_APP_API_URL;

const AssignedGoalsSection = () => {
    const navigate = useNavigate();
    const showToast = useToast();

    const [assignedGoals, setAssignedGoals] = useState([]);
    const [quarters, setQuarters] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState('');
    
    const [goalDescriptions, setGoalDescriptions] = useState({});
    const [attachmentLinks, setAttachmentLinks] = useState({});
    const [selfRatings, setSelfRatings] = useState({});
    const [communicatedStatuses, setCommunicatedStatuses] = useState({});
    
    const [isInstructionsOpen, setInstructionsOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [loadingGoalId, setLoadingGoalId] = useState(null);
    
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    const [currentGoalId, setCurrentGoalId] = useState(null);
    const [initialText, setInitialText] = useState('');

    const empId = localStorage.getItem('employeeId');

    useEffect(() => {
        if (!empId) return;

        // Fetch quarters
        axios.get(`${apiUrl}/api/quarters`)
            .then(response => {
                setQuarters(response.data || []);
            })
            .catch(err => console.error('Error fetching quarters:', err));

        // Fetch assigned goals for this user
        fetchAssignedGoals(empId);
    }, [empId]);

    const fetchAssignedGoals = async (employeeId) => {
        try {
            const response = await axios.get(`${apiUrl}/api/employee-goals/${employeeId}`);
            if (Array.isArray(response.data)) {
                // Filter goals that are valid and have description
                const validGoals = response.data.filter(g => g && (g.goal_description || g.goal_id || g._id));
                const uniqueGoals = Array.from(new Map(validGoals.map(goal => [goal._id || goal.id, goal])).values());
                setAssignedGoals(uniqueGoals);
            } else {
                setAssignedGoals([]);
            }
        } catch (err) {
            console.error('Error fetching assigned goals:', err);
            setAssignedGoals([]);
        }
    };

    const openTextModal = (field, goalId, existingText) => {
        setCurrentField(field);
        setCurrentGoalId(goalId);
        if (field === 'description') {
            setInitialText(goalDescriptions[goalId] || '');
        } else if (field === 'attachment') {
            setInitialText(attachmentLinks[goalId] || '');
        }
        setModalIsOpen(true);
    };

    const handleTextModalSubmit = (submittedText) => {
        if (currentField === 'description' && currentGoalId !== null) {
            setGoalDescriptions(prev => ({ ...prev, [currentGoalId]: submittedText }));
        } else if (currentField === 'attachment' && currentGoalId !== null) {
            setAttachmentLinks(prev => ({ ...prev, [currentGoalId]: submittedText }));
        }
        setModalIsOpen(false);
        setInitialText('');
    };

    const handleSubmitAssignedGoalRow = async (goalId) => {
        if (!selectedQuarter) {
            showToast('Please select a quarter before submitting', 'warning');
            return;
        }

        const description = (goalDescriptions[goalId] || '').trim();
        const rating = selfRatings[goalId] || '';
        const communicated = communicatedStatuses[goalId] || '';

        let newErrors = {};
        if (!description) {
            newErrors[goalId] = 'Description text is required';
            showToast('Description text is mandatory for submission.', 'warning');
            setErrors(newErrors);
            return;
        }
        if (!rating) {
            newErrors[goalId] = 'Self rating is required';
            showToast('Self Rating is mandatory for submission.', 'warning');
            setErrors(newErrors);
            return;
        }
        if (!communicated) {
            newErrors[goalId] = 'Communicated status is required';
            showToast('Communicated status is mandatory for submission.', 'warning');
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setLoadingGoalId(goalId);
        try {
            await axios.post(`${apiUrl}/api/update-goal-row`, {
                goalId,
                self_assessment: goalDescriptions[goalId] || '',
                attachments: attachmentLinks[goalId] || '',
                self_rating: selfRatings[goalId] || '',
                communicated: communicatedStatuses[goalId] || '',
                employeeId: empId,
                quarter: selectedQuarter,
                is_submitted: true
            });
            setAssignedGoals(prev => prev.filter(g => (g._id || g.id) !== goalId));
            showToast('Goal submitted successfully.', 'success');
        } catch (err) {
            if (err.response && err.response.status === 400) {
                showToast('Goal has already been submitted for this quarter.', 'error');
            } else {
                showToast('Error updating goal.', 'error');
            }
        } finally {
            setLoadingGoalId(null);
        }
    };

    // Filter valid goals that actually have goal descriptions
    const validDisplayGoals = assignedGoals.filter(g => g && g.goal_description);

    return (
        <div className="employee-body" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            {loadingGoalId && <LoadingOverlay />}
            <div className="goals-header-container">
                <h2>Assigned Goals</h2>
                <div className="quarter-history-container">
                    <div className="quarter-selector">
                        <label htmlFor="quarter">Select Quarter <span className="required-star" style={{ color: 'red' }}>*</span>:</label>
                        <select
                            id="quarter"
                            value={selectedQuarter}
                            onChange={(e) => setSelectedQuarter(e.target.value)}
                        >
                            <option value="">Select Quarter</option>
                            {quarters.map((quarter) => (
                                <option key={quarter.id || quarter._id || quarter.quarter} value={quarter.quarter}>
                                    {quarter.quarter}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button className="history-btn" onClick={() => navigate('/history')}>
                        <FontAwesomeIcon icon={faHistory} /> Submitted
                    </button>
                    <button className="instructions-btn" onClick={() => setInstructionsOpen(true)} title="Instructions">
                        <FaInfoCircle size={24} />
                    </button>
                </div>
            </div>

            <div className="table-container pms-table-wrap" style={{ width: '100%', overflowX: 'auto' }}>
                <table className="goals-table pms-table" style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th>GOAL DESCRIPTION</th>
                            <th>DESCRIPTION TEXT <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>ATTACHMENT</th>
                            <th>SELF RATING <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>COMMUNICATED <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>SUBMIT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {validDisplayGoals.length > 0 ? (
                            validDisplayGoals.map((goal) => {
                                const gId = goal._id || goal.id;
                                return (
                                    <tr key={gId}>
                                        <td style={{ fontWeight: '500' }}>{goal.goal_description}</td>
                                        <td>
                                            <input 
                                                type="text"
                                                value={goalDescriptions[gId] || ''}
                                                onChange={(e) => setGoalDescriptions(prev => ({ ...prev, [gId]: e.target.value }))}
                                                onClick={() => openTextModal('description', gId, goalDescriptions[gId] || '')}
                                                placeholder="Enter description"
                                            />
                                            {errors[gId] && <span className="error-message">{errors[gId]}</span>}
                                        </td>
                                        <td>
                                            <input 
                                                type="text"
                                                value={attachmentLinks[gId] || ''}
                                                onChange={(e) => setAttachmentLinks(prev => ({ ...prev, [gId]: e.target.value }))}
                                                onClick={() => openTextModal('attachment', gId, attachmentLinks[gId] || '')}
                                                placeholder="Enter attachment link"
                                            />
                                        </td>
                                        <td>
                                            <select
                                                value={selfRatings[gId] || ''}
                                                onChange={(e) => setSelfRatings(prev => ({ ...prev, [gId]: e.target.value }))}
                                            >
                                                <option value="">Select</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                                <option value="5">5</option>
                                            </select>
                                            {errors[gId] && <span className="error-message">{errors[gId]}</span>}
                                        </td>
                                        <td>
                                            <select
                                                value={communicatedStatuses[gId] || ''}
                                                onChange={(e) => setCommunicatedStatuses(prev => ({ ...prev, [gId]: e.target.value }))}
                                            >
                                                <option value="">Select</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </select>
                                            {errors[gId] && <span className="error-message">{errors[gId]}</span>}
                                        </td>
                                        <td>
                                            <button 
                                                onClick={() => handleSubmitAssignedGoalRow(gId)} 
                                                disabled={loadingGoalId === gId}
                                                style={{
                                                    backgroundColor: '#e67320',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    padding: '6px 18px',
                                                    borderRadius: '20px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {loadingGoalId === gId ? 'Submitting...' : 'Submit'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                    No assigned goals found for your account.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* TextModal for editing long description/attachment */}
            <TextModal
                isOpen={modalIsOpen}
                onRequestClose={() => setModalIsOpen(false)}
                onTextSubmit={handleTextModalSubmit}
                initialText={initialText}
                field={currentField}
            />

            {/* Instructions Modal */}
            <div className={`instructions-modal ${isInstructionsOpen ? 'open' : ''}`}>
                <h3>Goal Submission and Review Process</h3>
                <ol>
                    <li>Goals will be dynamically fetched in your dashboard once assigned to you.</li>
                    <li>You need to select the particular quarter from the dropdown for which you are submitting the goal.</li>
                    <li>When entering your goal description, click the text input to use the text editor.</li>
                    <li>To upload certifications or attachments, store the files in OneDrive and paste the shared link into the "Attachment" field.</li>
                    <li>Self-rating must be selected on a scale of 1 to 5.</li>
                    <li>Indicate whether you have communicated this goal to your reviewer.</li>
                    <li>Click "Submit" to send your goal for review.</li>
                </ol>
                <button onClick={() => setInstructionsOpen(false)} style={{ marginTop: '16px', padding: '6px 16px', cursor: 'pointer' }}>
                    Close
                </button>
            </div>
        </div>
    );
};

export default AssignedGoalsSection;
